import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { printPackageTrackingIdError } from '@returnmates/client-core/src/constants/errors'
import { SourceType } from '@returnmates/client-core/src/constants/sourceType'
import { PackagePageData } from '@returnmates/client-core/src/graphql/custom/api'
import {
  CreatePolybag,
  PackageStatus,
  PackageType,
  Partner,
} from '@returnmates/client-core/src/graphql/generated/api'
import {
  getOnfleetWorkers,
  getPackageServiceTypes,
  getPartners as getPartnersSelector,
} from '@returnmates/client-core/src/selectors/admin'
import { getLocations } from '@returnmates/client-core/src/selectors/location'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { CustomAdminPackage } from '@returnmates/client-core/src/type/pickups'
import camelToUnderscore from '@returnmates/client-core/src/utils/camelToUnderscore'
import cleanIds from '@returnmates/client-core/src/utils/cleanIds'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import getUniqueList from '@returnmates/client-core/src/utils/getUniqueList'
import {
  groupPackagesByAttribute,
  hydrateMultipleLabelTemplate,
  hydrateSingleLabelTemplate,
} from '@returnmates/client-core/src/utils/hydrateLabelTemplate'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Button from '@returnmates/ui-core/src/components/Button'
import SearchToolbar from '@returnmates/ui-core/src/components/SearchToolbar'
import moment from 'moment'
import {
  memo,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper'

import { Context } from '../../../pages/Main'
import { Filter } from '../../../pages/Packages'
import Calendar from '../../Calendar'
import CheckboxFilter from '../../CheckboxFilter'
import PlusIcon from '../../images/icons/plus'
import { getMinPartnerSchedulerLimit, getPartnerNamesWithGreaterLimit } from '../../utils'
import {
  checkboxFilter,
  checkboxPackageProblemReasonFilter,
  labelTypeFilter,
  NORDSTROM_PARTNER,
  pickupSearchFields,
} from '../constants'
import useStyles from '../styles'
import { getNordstromLabelTemplate, hydratePolybagLabels } from '../utils'
import MoreFilters from './MoreFilters'
import PrintLabels from './PrintLabels'

interface PartnerGroup {
  labelTemplate?: string | null
  labelPackageCount?: number
  labelPackageGroup?: string | null
  packages: PackagePageData[] | CustomAdminPackage[]
}
interface Props {
  setSelectedRowsProp?: (values: string[]) => void
  selectedRowsProp?: string[]
  onFilterUpdate?: (val: Filter) => void
  refreshPackages?: () => void
  defaultFilter?: {
    [key: string]: boolean
  }
  packagesData: PackagePageData[]
  isTripUpdatedLoading: boolean
  setIsTripUpdateLoading: (val: boolean) => void
  isTriplessEnabledHub?: boolean
  handleAddPackageModalOpen?: () => void
}

function Toolbar({
  selectedRowsProp,
  setSelectedRowsProp,
  onFilterUpdate,
  refreshPackages,
  defaultFilter,
  packagesData,
  isTripUpdatedLoading,
  setIsTripUpdateLoading,
  isTriplessEnabledHub = false,
  handleAddPackageModalOpen,
}: Props) {
  const classes = useStyles({ isTriplessEnabledHub })
  const { currentHubId } = useContext(Context)
  const [searchValue, setSearchValue] = useState('')
  const [searchField, setSearchField] = useState('addressName')
  const [isHideConsolidationChecked, setIsHideConsolidationChecked] = useState(false)
  const [isHideUnsortedChecked, setIsHideUnsortedChecked] = useState(true)
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: boolean }>({
    ...defaultFilter,
  })
  const [selectedLabelValues, setSelectedLabelValues] = useState<{ [key: string]: boolean }>({})
  const [selectedRows, setSelectedRows] = useState<Array<string>>([])

  const packagesToPrint = useRef<PackagePageData[]>([])

  const partners = useSelector(getPartnersSelector)
  const onfleetWorkers = useSelector(getOnfleetWorkers)
  const packageServiceTypes = useSelector(getPackageServiceTypes)
  const locations = useSelector(getLocations)
  const dispatch = useDispatch()

  const driverValues = useMemo(
    () => onfleetWorkers?.map(driver => ({ key: driver.id, label: driver.name })),
    [onfleetWorkers],
  )

  const partnerValues = useMemo<Array<{ key: string; label: string }>>(() => {
    const partnerOptions = partners.reduce((acc, partner) => {
      if (partner.code !== 'rm') {
        return [
          ...acc,
          {
            key: partner.code as string,
            label: partner.name as string,
          },
        ]
      }

      return acc
    }, [])

    return [{ key: 'rm', label: 'Returnmates' }, ...partnerOptions]
  }, [partners])

  const locationValues = useMemo(
    () =>
      locations?.map(location => ({
        key: location.id,
        label: location.name,
      })),
    [locations],
  )

  const packageServiceTypesValues = useMemo(
    () =>
      packageServiceTypes?.map(packageServiceType => ({
        key: packageServiceType.id,
        label: packageServiceType.id,
      })),
    [packageServiceTypes],
  )

  const printBarcode = useCallback(
    async (zpl: string | Array<string>) => {
      console.log(zpl)
      try {
        const browserPrint = new ZebraBrowserPrintWrapper()
        const defaultPrinter = await browserPrint.getDefaultPrinter()
        browserPrint.setPrinter(defaultPrinter)
        const printerStatus = await browserPrint.checkPrinterStatus()

        if (printerStatus.isReadyToPrint) {
          if (typeof zpl === 'string') {
            browserPrint.print(zpl)
          } else {
            zpl.forEach(template => browserPrint.print(template))
          }
        } else {
          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: printerStatus.errors,
            }),
          )
        }
      } catch (error) {
        console.log('error', error)
      }
    },
    [dispatch],
  )

  const onDateUpdate = useCallback(
    async (date: string) => {
      setIsTripUpdateLoading(true)

      try {
        await createAsyncAction(
          dispatch,
          actions.reschedule.request({
            packageIds: selectedRows,
            date: moment(date).format('YYYY-MM-DD'),
            updateSource: SourceType.WILSON,
          }),
        )

        setSelectedRowsProp?.([])
        refreshPackages?.()
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        setIsTripUpdateLoading(false)
      }
    },
    [setIsTripUpdateLoading, dispatch, selectedRows, setSelectedRowsProp, refreshPackages],
  )

  const defaultLabelTemplate = useMemo(() => partners.find(p => p.code === 'rm')?.labelTemplate, [
    partners,
  ])

  const getPackageTrackingIdZplTemplate = useCallback(
    (trackingId: string) =>
      `^XA^LH10,10^LS0^MTD^PW1200^CF0,60^FO20,50^FD^CF0,40^FO20,133^FS^FO20,273^FS^FB887,6,22,L^FO20,325^FS^FO20,40^FDPACKAGE TRACKING ID: ${trackingId} ^FS^FO20,100^BY2,2,120^BC,,N^FD${trackingId}^FS^XZ`,
    [],
  )

  const handleTrackingIdPrint = useCallback(() => {
    const selectedPackages = [...packagesToPrint.current]
    const packagesTrackingId: string[] = []
    const packagesWithoutTrackingId: string[] = []
    const labelsToPrint: string[] = []

    selectedPackages.forEach(pack =>
      pack.trackingId
        ? packagesTrackingId.push(pack.trackingId)
        : packagesWithoutTrackingId.push(pack.id),
    )

    packagesTrackingId.forEach(trackindId =>
      labelsToPrint.push(getPackageTrackingIdZplTemplate(trackindId)),
    )

    printBarcode(labelsToPrint)

    if (packagesWithoutTrackingId.length) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: printPackageTrackingIdError(packagesWithoutTrackingId),
        }),
      )
    }

    setSelectedRowsProp?.([])
  }, [dispatch, getPackageTrackingIdZplTemplate, printBarcode, setSelectedRowsProp])

  const handlePackageIDLabelsPrint = useCallback(() => {
    const selectedPackages = [...packagesToPrint.current]

    const packagesByPartner = selectedPackages.reduce(
      (acc: { [key: string]: PartnerGroup }, pack: CustomAdminPackage) => {
        const { partnerName } = pack

        if (partnerName) {
          const currentPartner = partners.find(p => p.name === partnerName)

          if (!acc[partnerName]) {
            acc[partnerName] = {
              labelTemplate: currentPartner?.labelTemplate ?? defaultLabelTemplate,
              labelPackageCount: currentPartner?.labelPackageCount,
              labelPackageGroup: currentPartner?.labelPackageGroup,
              packages: [],
            }
          }

          acc[partnerName] = {
            ...acc[partnerName],
            packages: [...acc[partnerName].packages, pack],
          }
        } else {
          acc['rm'] = {
            ...acc['rm'],
            packages: [...acc['rm'].packages, pack],
          }
        }

        return acc
      },
      {},
    )

    const labelsToPrint: string[] = []

    Object.keys(packagesByPartner).forEach(partnerName => {
      const currentPartner = packagesByPartner[partnerName]
      if (currentPartner.labelPackageGroup) {
        const groupsByAttribute = groupPackagesByAttribute(
          currentPartner.packages,
          currentPartner.labelPackageGroup,
          currentPartner.labelPackageCount || 1,
        )

        const hydratedMultipleLabels = groupsByAttribute.map(packGroup => {
          const pck = packGroup[0]
          const modifiedPartnerAttr: { rma_id?: string; order_number?: string } = {}
          let labelTemplate = currentPartner.labelTemplate ?? (defaultLabelTemplate as string)
          const packagePartnerAttributes = pck.partnerAttributes
            ? JSON.parse(pck.partnerAttributes)
            : null

          for (const key in packagePartnerAttributes) {
            //@ts-ignore
            modifiedPartnerAttr[camelToUnderscore(key)] = packagePartnerAttributes[key]
          }

          if (pck.partnerCode?.toLocaleLowerCase() === NORDSTROM_PARTNER) {
            labelTemplate = getNordstromLabelTemplate(
              labelTemplate,
              modifiedPartnerAttr?.rma_id,
              modifiedPartnerAttr?.order_number,
            )
          }

          return hydrateMultipleLabelTemplate(
            labelTemplate,
            packGroup,
            currentPartner.labelPackageCount || 1,
          )
        })

        labelsToPrint.push(...hydratedMultipleLabels)
      } else {
        const isMultipleLabel = currentPartner.labelTemplate?.includes('{Package[')

        if (isMultipleLabel) {
          const hydratedMultipleLabels = currentPartner.packages.map(pack =>
            hydrateMultipleLabelTemplate(
              currentPartner.labelTemplate ?? (defaultLabelTemplate as string),
              [pack],
              currentPartner.labelPackageCount || 1,
            ),
          )

          labelsToPrint.push(...hydratedMultipleLabels)
        } else {
          const hydratedSingleLabels = currentPartner.packages.map(pack =>
            hydrateSingleLabelTemplate(currentPartner.labelTemplate as string, pack),
          )

          labelsToPrint.push(...hydratedSingleLabels)
        }
      }
    })

    printBarcode(labelsToPrint)

    setSelectedRowsProp?.([])
  }, [printBarcode, setSelectedRowsProp, partners, defaultLabelTemplate])

  const handlePolybagIDsPrint = useCallback(
    async count => {
      try {
        const createdPolybags: CreatePolybag[] = await createAsyncAction(
          dispatch,
          actions.createPolybags.request({ count }),
        )

        const createdPolybagsIds = createdPolybags.reduce((acc, polybag) => {
          if (polybag.success) {
            return [...acc, polybag.id]
          }

          return acc
        }, [])

        if (createdPolybagsIds && createdPolybagsIds.length) {
          const hydratedPolybagIDsLabels = hydratePolybagLabels(createdPolybagsIds)

          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.SUCCESS,
              message: `The printing of ${createdPolybags.length} polybag(s) has started`,
            }),
          )

          printBarcode(hydratedPolybagIDsLabels)
        }
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      }
    },
    [dispatch, printBarcode],
  )

  const handleChangeSearch = useCallback(e => {
    const searchValue = e.target.value
    setSearchValue(searchValue)
  }, [])

  const onSearchFieldChange = useCallback((searchField: string) => {
    setSearchField(searchField)
  }, [])

  const onHideConsolidationsSelect = useCallback(() => {
    setIsHideConsolidationChecked(val => !val)
  }, [])

  const onHideUnsortedSelect = useCallback(() => {
    setIsHideUnsortedChecked(val => !val)
  }, [])

  const onChangeLabelFilter = useCallback(
    newSelection => {
      setSelectedLabelValues({
        ...selectedLabelValues,
        ...newSelection,
      })
    },
    [selectedLabelValues],
  )

  const onChangeFilter = useCallback(
    newSelection => {
      setSelectedValues({
        ...selectedValues,
        ...newSelection,
      })
    },
    [selectedValues],
  )

  const statusFilter = useMemo(
    () =>
      checkboxFilter.values.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return [...acc, item.key]
        }
        return acc
      }, []),
    [selectedValues],
  )

  const problemReasonFilter = useMemo(
    () =>
      checkboxPackageProblemReasonFilter.values.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return [...acc, item.key]
        }
        return acc
      }, []),
    [selectedValues],
  )

  const labelFilter = useMemo(
    () =>
      labelTypeFilter.values.reduce((acc, item) => {
        if (selectedLabelValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedLabelValues],
  )

  const driverFilter = useMemo(
    () =>
      (driverValues || []).reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [driverValues, selectedValues],
  )

  const partnerFilter = useMemo(
    () =>
      partnerValues.reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedValues, partnerValues],
  )

  const serviceTypeFilter = useMemo(
    () =>
      (packageServiceTypesValues || []).reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [packageServiceTypesValues, selectedValues],
  )

  const locationFilter = useMemo(
    () =>
      (locationValues || []).reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [locationValues, selectedValues],
  )

  const searchFields = useMemo(() => pickupSearchFields, [])

  const taskTypesValues = useMemo(
    () => [
      { key: PackageType.PICKUP, label: 'Pickup' },
      { key: PackageType.DROPOFF, label: 'Delivery' },
    ],
    [],
  )

  const taskTypesFilter = useMemo(
    () =>
      (taskTypesValues || []).reduce((acc, item) => {
        if (selectedValues[item.key]) {
          return item.key
        }
        return acc
      }, null),
    [selectedValues, taskTypesValues],
  )

  useEffect(() => {
    updateFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    driverFilter,
    isHideConsolidationChecked,
    labelFilter,
    problemReasonFilter,
    onFilterUpdate,
    partnerFilter,
    statusFilter,
    serviceTypeFilter,
    locationFilter,
    isHideUnsortedChecked,
  ])

  useEffect(() => {
    if (!searchValue.length) {
      updateFilters()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  const updateFilters = useCallback(() => {
    const filtersObject = {
      status: statusFilter,
      labelType: labelFilter,
      problemReason: problemReasonFilter,
      partnerCode: partnerFilter,
      workerId: driverFilter,
      hideConsolidation: isHideConsolidationChecked,
      hideUnsorted: isHideUnsortedChecked,
      serviceType: serviceTypeFilter,
      locationId: locationFilter,
      type: taskTypesFilter,
    }

    if (searchValue.length) {
      Object.assign(filtersObject, { [searchField]: searchValue })
    }

    if (selectedValues[PackageStatus.PROBLEM]) {
      setIsHideUnsortedChecked(false)
    }

    onFilterUpdate?.(filtersObject)
  }, [
    statusFilter,
    labelFilter,
    partnerFilter,
    driverFilter,
    isHideConsolidationChecked,
    isHideUnsortedChecked,
    serviceTypeFilter,
    locationFilter,
    taskTypesFilter,
    searchValue,
    selectedValues,
    onFilterUpdate,
    searchField,
    problemReasonFilter,
  ])

  const updateRefsOnUnselect = useCallback(
    (tripsRef: RefObject<PackagePageData[]>) => {
      return tripsRef?.current?.filter(pack => selectedRows?.includes(pack.id)) || []
    },
    [selectedRows],
  )

  const clearFilters = useCallback(() => {
    setSelectedValues({ ...defaultFilter })
    setIsHideUnsortedChecked(true)
    setSelectedLabelValues({})
    onFilterUpdate?.({} as Filter)
  }, [defaultFilter, onFilterUpdate])

  const activeFilters = useMemo(
    () =>
      Object.keys({ ...selectedLabelValues, ...selectedValues }).filter(
        key => selectedValues[key] || selectedLabelValues[key],
      ),
    [selectedLabelValues, selectedValues],
  )

  const isClearFiltersAvailable = useMemo(() => {
    return activeFilters.length > 0
  }, [activeFilters])

  const isTaskTypeFilterEnabled = useMemo(
    () => selectedValues[PackageType.DROPOFF] || selectedValues[PackageType.PICKUP],
    [selectedValues],
  )

  const selectedPackages = useMemo(() => {
    if (selectedRows?.length && packagesData) {
      return packagesData.filter(pack => selectedRows.includes(pack.id))
    }
    return []
  }, [selectedRows, packagesData])

  const selectedPackagePartners = useMemo(() => {
    const uniquePartnerCodes = Array.from(
      new Set(selectedPackages.map(pack => pack.partnerCode).filter(Boolean)),
    )
    return uniquePartnerCodes
      .map(partnerCode => partners.find(p => p.code === partnerCode))
      .filter((p): p is Partner => Boolean(p))
  }, [partners, selectedPackages])

  const minPartnerSchedulerLimit: number | null = useMemo(
    () => getMinPartnerSchedulerLimit(selectedPackagePartners),
    [selectedPackagePartners],
  )

  const partnersWithGreaterLimit: string = useMemo(
    () => getPartnerNamesWithGreaterLimit(selectedPackagePartners, minPartnerSchedulerLimit),
    [minPartnerSchedulerLimit, selectedPackagePartners],
  )

  useEffect(() => {
    selectedRows?.reduce(
      (acc, selectedPackId) => {
        const selectedPackageOnCurrentPage = packagesData?.find(pack => pack.id === selectedPackId)

        packagesToPrint.current = updateRefsOnUnselect(packagesToPrint)

        if (selectedPackageOnCurrentPage) {
          const currentPackagesToPrint = [...acc.packagesToPrint, selectedPackageOnCurrentPage]

          const uniqueSelectedPackagesList = getUniqueList(
            [...packagesToPrint.current, ...currentPackagesToPrint],
            'id',
          )

          packagesToPrint.current = uniqueSelectedPackagesList

          return {
            ...acc,
            packagesToPrint: currentPackagesToPrint,
          }
        } else {
          return acc
        }
      },
      {
        packagesToPrint: [],
      },
    )
  }, [packagesData, selectedRows, updateRefsOnUnselect])

  useEffect(() => {
    setSelectedRows(cleanIds(selectedRowsProp))
  }, [selectedRowsProp])

  useEffect(() => {
    if (window.location.search) {
      const searchParams = new URLSearchParams(window.location.search)
      const packageId = searchParams.get('packageId')
      const tripType = searchParams.get('tripType')
      const status = searchParams.get('status')
      const partner = searchParams.get('partner')

      if (packageId) {
        setSearchValue(packageId)
        setSearchField('packageId')
      }

      const queryParamsFilters = {
        ...(tripType && { [tripType]: true }),
        ...(status && { [status]: true }),
        ...(partner && { [partner]: true }),
      }

      if (Object.keys(queryParamsFilters).length) {
        onChangeFilter(queryParamsFilters)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={classes.toolbar}>
      <div className={classes.toolbarLeftPart}>
        <SearchToolbar
          value={searchValue}
          onInputChange={handleChangeSearch}
          onSearchFieldChange={onSearchFieldChange}
          isShowSearchButton
          onSearch={updateFilters}
          searchField={searchField}
          searchFields={searchFields}
        />

        <Calendar
          onDateUpdate={onDateUpdate}
          isLoading={isTripUpdatedLoading}
          show={Boolean(selectedRows?.length)}
          isTaskTypeFilterEnabled={isTaskTypeFilterEnabled}
          schedulerLimit={minPartnerSchedulerLimit}
          notes={partnersWithGreaterLimit}
        />

        <CheckboxFilter
          values={checkboxFilter.values}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          isAllAvailable
          label="Status"
        />
        <CheckboxFilter
          values={checkboxPackageProblemReasonFilter.values}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          label="Package Problem Reason"
        />
        <CheckboxFilter
          values={taskTypesValues}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          isAllAvailable
          onlyOneSelect
          label="Task Type"
        />
        <CheckboxFilter
          values={labelTypeFilter.values}
          selectedValues={selectedLabelValues}
          onChange={onChangeLabelFilter}
          isAllAvailable
          onlyOneSelect
          label="Label Type"
        />
        <CheckboxFilter
          values={partnerValues}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          isAllAvailable
          onlyOneSelect
          label="Partner"
        />
        <CheckboxFilter
          values={driverValues || []}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          isAllAvailable
          onlyOneSelect
          withSearch
          searchPlaceholder="Search driver"
          label="Driver"
        />
        <CheckboxFilter
          values={packageServiceTypesValues || []}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          onlyOneSelect
          isAllAvailable
          label="Service Type"
        />
        <CheckboxFilter
          values={locationValues || []}
          selectedValues={selectedValues}
          onChange={onChangeFilter}
          onlyOneSelect
          isAllAvailable
          disabled={!currentHubId}
          label="Location"
        />
        {isClearFiltersAvailable ? (
          <Button label="Clear Filters" className={classes.clearFilters} onClick={clearFilters} />
        ) : null}
      </div>
      <div className={classes.toolbarRightPart}>
        {isTriplessEnabledHub ? (
          <Button
            label="Add Package"
            onClick={handleAddPackageModalOpen}
            startIcon={<PlusIcon className={classes.plusIcon} />}
            className={classes.addPackageButton}
          />
        ) : null}
        <PrintLabels
          onPackageIDLabelsClick={handlePackageIDLabelsPrint}
          handleTrackingIdPrint={handleTrackingIdPrint}
          handlePolybagIDsPrint={handlePolybagIDsPrint}
          showOptions={Boolean(selectedRows?.length)}
          showPolybagOption
        />
        <MoreFilters
          isHideConsolidationChecked={isHideConsolidationChecked}
          isHideUnsortedChecked={isHideUnsortedChecked}
          onHideConsolidationsSelect={onHideConsolidationsSelect}
          onHideUnsortedSelect={onHideUnsortedSelect}
        />
      </div>
    </div>
  )
}

export default memo(Toolbar)
